<template>
  <v-form v-model="valid" @submit.prevent="submit">
    <v-card variant="outlined">
      <v-card-title class="d-flex align-center bg-primary-lighten-5">
        <v-icon start>mdi-card-account-details</v-icon>
        {{
          contactDetail.id ? "Contact detail bewerken" : "Nieuw contact detail"
        }}
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Contact Detail Type Selection -->
        <v-select
          v-model="contactDetail.type"
          :items="contactDetailTypes"
          label="Type contact detail"
          variant="outlined"
          item-title="type_name"
          item-value="type"
          density="comfortable"
          :disabled="contactDetail.id != null"
          :rules="[rules.required]"
          prepend-inner-icon="mdi-format-list-bulleted-type"
          class="mb-3"
          @update:model-value="resetValues"
        />

        <!-- Email Fields -->
        <template v-if="contactDetail.type === 'email'">
          <v-text-field
            v-model="contactDetail.email"
            label="E-mail adres"
            placeholder="bijvoorbeeld@email.com"
            variant="outlined"
            density="comfortable"
            :rules="[rules.required, rules.email]"
            prepend-inner-icon="mdi-email"
            class="mb-3"
            type="email"
          />
        </template>

        <!-- Phone Number Fields -->
        <template v-if="contactDetail.type === 'phone_number'">
          <v-text-field
            v-model="contactDetail.phone_number"
            label="Telefoonnummer"
            placeholder="06xxxxxxxx of 020xxxxxxx"
            variant="outlined"
            density="comfortable"
            :rules="[rules.required, rules.phoneNumber]"
            prepend-inner-icon="mdi-phone"
            class="mb-3"
            @input="validatePhoneNumber"
          />

          <v-select
            v-model="contactDetail.phone_type"
            :items="phoneTypeOptions"
            label="Type telefoonnummer"
            variant="outlined"
            item-title="label"
            item-value="value"
            density="comfortable"
            :rules="[rules.required]"
            class="mb-3"
          >
            <template #prepend-inner>
              <v-icon :icon="getPhoneTypeIcon(contactDetail.phone_type)" />
            </template>

            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps">
                <template #prepend>
                  <v-icon :icon="item.raw.icon" :color="item.raw.color" />
                </template>
              </v-list-item>
            </template>
          </v-select>

          <v-textarea
            v-model="contactDetail.meta"
            label="Extra informatie (optioneel)"
            placeholder="Bijvoorbeeld: Best bereikbaar tussen 9-17u, WhatsApp beschikbaar"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
            prepend-inner-icon="mdi-information"
            class="mb-3"
            counter="100"
            :rules="[rules.maxLength(100)]"
          />

          <!-- Phone validation feedback -->
          <v-alert
            v-if="phoneValidationMessage"
            :type="phoneValidationType"
            variant="tonal"
            class="mb-3"
            :text="phoneValidationMessage"
          />
        </template>

        <!-- Address Fields -->
        <template v-if="contactDetail.type === 'address'">
          <v-row>
            <v-col cols="8">
              <v-text-field
                v-model="contactDetail.street"
                label="Straat"
                placeholder="Hoofdstraat"
                variant="outlined"
                density="comfortable"
                :rules="[rules.required]"
                prepend-inner-icon="mdi-road"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="contactDetail.number"
                label="Nummer"
                placeholder="123"
                variant="outlined"
                density="comfortable"
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="contactDetail.postal_code"
                label="Postcode"
                placeholder="1234 AB"
                variant="outlined"
                density="comfortable"
                :rules="[rules.required, rules.postalCode]"
              />
            </v-col>
            <v-col cols="8">
              <v-text-field
                v-model="contactDetail.city"
                label="Plaats"
                placeholder="Amsterdam"
                variant="outlined"
                density="comfortable"
                :rules="[rules.required]"
                prepend-inner-icon="mdi-city"
              />
            </v-col>
          </v-row>
        </template>

        <!-- Note Fields -->
        <template v-if="contactDetail.type === 'note'">
          <v-textarea
            v-model="contactDetail.note"
            label="Notitie"
            placeholder="Voer hier je notitie in..."
            variant="outlined"
            density="comfortable"
            :rules="[rules.required]"
            prepend-inner-icon="mdi-note-text"
            rows="3"
            auto-grow
            class="mb-3"
          />

          <v-select
            v-model="contactDetail.note_type"
            :items="noteTypeOptions"
            label="Type notitie"
            variant="outlined"
            item-title="label"
            item-value="value"
            density="comfortable"
            :rules="[rules.required]"
            prepend-inner-icon="mdi-tag"
            class="mb-3"
          />

          <v-text-field
            v-model="contactDetail.note_date"
            label="Datum"
            variant="outlined"
            density="comfortable"
            type="date"
            prepend-inner-icon="mdi-calendar"
            class="mb-3"
          />
        </template>

        <!-- Primary Switch (not for notes) -->
        <v-switch
          v-if="contactDetail.type !== 'note'"
          v-model="contactDetail.primary"
          label="Primair maken"
          color="primary"
          class="mb-3"
          hide-details
        />

        <!-- Error Message -->
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          class="mb-3"
          :text="error"
        />
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-btn v-if="!popup" variant="outlined" @click="goBack">
          Annuleren
        </v-btn>
        <v-spacer />
        <v-btn
          type="submit"
          color="primary"
          variant="elevated"
          :loading="loading"
          :disabled="!valid"
        >
          {{ contactDetail.id ? "Bijwerken" : "Opslaan" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useFormsStore } from "@/stores/formsStore";
import { useAlertStore } from "@/stores/alertStore";

const props = defineProps({
  contact_detail: {
    type: Object,
    default: () => ({}),
  },
  popup: {
    type: Boolean,
    default: false,
  },
});

// Store instances
const router = useRouter();
const formsStore = useFormsStore();
const alertStore = useAlertStore();

// Reactive data
const valid = ref(false);
const loading = ref(false);
const error = ref(null);
const contactDetail = ref({});
const phoneValidationMessage = ref("");
const phoneValidationType = ref("info");

// Form options
const contactDetailTypes = [
  { type_name: "E-mail", type: "email" },
  { type_name: "Telefoonnummer", type: "phone_number" },
  { type_name: "Adres", type: "address" },
  { type_name: "Notitie", type: "note" },
];

const phoneTypeOptions = computed(() => [
  {
    label: "Mobiel",
    value: "mobile",
    icon: "mdi-cellphone",
    color: "green",
  },
  {
    label: "Vast",
    value: "vast",
    icon: "mdi-phone",
    color: "blue",
  },
  {
    label: "Fax",
    value: "fax",
    icon: "mdi-fax",
    color: "orange",
  },
  {
    label: "Overig",
    value: "overig",
    icon: "mdi-phone-outline",
    color: "grey",
  },
]);

const noteTypeOptions = [
  { label: "Algemeen", value: "general" },
  { label: "Beschikbaarheid", value: "availability" },
  { label: "Rol/Functie", value: "role" },
  { label: "Voorkeur", value: "preference" },
  { label: "Belangrijk", value: "important" },
];

// Validation rules
const rules = {
  required: (value) => !!value || "Dit veld is verplicht",
  email: (value) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(value) || "Voer een geldig e-mailadres in";
  },
  phoneNumber: (value) => {
    if (!value) return "Telefoonnummer is verplicht";
    // Dutch phone validation patterns
    const patterns = {
      mobile: /^(06\d{8}|(\+31|0031)6\d{8})$/,
      landline: /^(0\d{9}|(\+31|0031)\d{9})$/,
      flexible: /^[\d\s\-().+]{4,15}$/, // Allow 4-15 characters for short service numbers
    };

    const cleaned = value.replace(/[\s\-().]/g, "");

    if (
      patterns.mobile.test(cleaned) ||
      patterns.landline.test(cleaned) ||
      patterns.flexible.test(cleaned)
    ) {
      return true;
    }

    return "Voer een geldig Nederlands telefoonnummer in";
  },
  postalCode: (value) => {
    const pattern = /^\d{4}\s?[A-Z]{2}$/i;
    return pattern.test(value) || "Voer een geldige postcode in (1234 AB)";
  },
  maxLength: (max) => (value) => {
    return (
      !value || value.length <= max || `Maximaal ${max} karakters toegestaan`
    );
  },
};

// Methods
const getPhoneTypeIcon = (phoneType) => {
  const option = phoneTypeOptions.value.find((opt) => opt.value === phoneType);
  return option ? option.icon : "mdi-phone";
};

const validatePhoneNumber = () => {
  if (!contactDetail.value.phone_number) {
    phoneValidationMessage.value = "";
    return;
  }

  const cleaned = contactDetail.value.phone_number.replace(/[\s\-().]/g, "");

  // Mobile number detection
  if (/^(06\d{8}|(\+31|0031)6\d{8})$/.test(cleaned)) {
    phoneValidationMessage.value = "Mobiel telefoonnummer gedetecteerd";
    phoneValidationType.value = "success";
    if (!contactDetail.value.phone_type) {
      contactDetail.value.phone_type = "mobile";
    }
    return;
  }

  // Landline detection
  if (/^(0\d{9}|(\+31|0031)\d{9})$/.test(cleaned)) {
    phoneValidationMessage.value = "Vast telefoonnummer gedetecteerd";
    phoneValidationType.value = "success";
    if (!contactDetail.value.phone_type) {
      contactDetail.value.phone_type = "vast";
    }
    return;
  }

  // Other valid format
  if (/^[\d\s\-().+]{4,15}$/.test(contactDetail.value.phone_number)) {
    phoneValidationMessage.value = "Telefoonnummer geaccepteerd als 'Overig'";
    phoneValidationType.value = "info";
    if (!contactDetail.value.phone_type) {
      contactDetail.value.phone_type = "overig";
    }
    return;
  }

  phoneValidationMessage.value = "Ongeldig telefoonnummer formaat";
  phoneValidationType.value = "error";
};

const resetValues = () => {
  const type = contactDetail.value.type;
  const contactId = contactDetail.value.contact_id;
  const id = contactDetail.value.id;

  contactDetail.value = {
    type,
    contact_id: contactId,
    id,
  };

  // Set defaults based on type
  if (type === "phone_number") {
    contactDetail.value.phone_type = "mobile";
  } else if (type === "note") {
    contactDetail.value.note_type = "general";
    contactDetail.value.note_date = new Date().toISOString().split("T")[0];
  }

  phoneValidationMessage.value = "";
  error.value = null;
};

const submit = async () => {
  if (!valid.value) return;

  try {
    loading.value = true;
    error.value = null;

    // API call logic would go here
    // For now, using placeholder
    const action = contactDetail.value.id ? "update" : "create";

    console.log(`Submitting contact detail ${action}:`, contactDetail.value);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    alertStore.success(
      `Contact detail succesvol ${contactDetail.value.id ? "bijgewerkt" : "aangemaakt"}`,
    );

    if (props.popup) {
      formsStore.$reset();
    } else {
      goBack();
    }
  } catch (err) {
    console.error("Error saving contact detail:", err);
    error.value = err.message || "Er is een fout opgetreden bij het opslaan";
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.go(-1);
};

// Watchers
watch(
  () => props.contact_detail,
  (newValue) => {
    contactDetail.value = { ...newValue };
    if (!contactDetail.value.type) {
      contactDetail.value.type = "email";
    }
  },
  { immediate: true },
);

// Lifecycle
onMounted(() => {
  contactDetail.value = { ...props.contact_detail };
  if (!contactDetail.value.type) {
    contactDetail.value.type = "email";
  }
});
</script>

<style scoped>
.v-form {
  width: 100%;
}
</style>
